import type { AppointmentTypeDto, BookingQuestion } from '@shared/core/appointment-types'
import { useState } from 'react'
import {
  addQuestion,
  canAddQuestion,
  copyQuestionsFrom,
  deleteQuestion,
  hasOwnerQuestions,
  initQuestionsEditor,
  moveQuestion,
  updateQuestion,
  type QuestionDraft,
  type QuestionsEditorState,
} from './booking-questions-editor.domain'
import type { OpenQuestionCard } from './booking-questions-editor.interface'

/** Initialized once at modal open: a later refetch must not swap the base revision (design D41). */
export function useBookingQuestionsEditor(appointmentType?: AppointmentTypeDto) {
  const [state, setState] = useState<QuestionsEditorState>(() => initQuestionsEditor(appointmentType))
  const [expectedRevision, setExpectedRevision] = useState(() => appointmentType?.booking_questions_revision ?? 0)
  const [openCard, setOpenCard] = useState<OpenQuestionCard | null>(null)
  const [copyMessage, setCopyMessage] = useState<string | null>(null)

  const closeCard = () => setOpenCard(null)

  return {
    state,
    replaceState: setState,
    expectedRevision,
    setExpectedRevision,
    openCard,
    copyMessage,
    canAddQuestion: canAddQuestion(state),
    hasOwnerQuestions: hasOwnerQuestions(state),
    openAddCard: () => setOpenCard({ mode: 'add' }),
    openEditCard: (key: string) => setOpenCard({ mode: 'edit', key }),
    closeCard,
    addQuestion: (draft: QuestionDraft) => {
      setState((current) => addQuestion(current, draft))
      closeCard()
    },
    updateQuestion: (key: string, draft: QuestionDraft) => {
      setState((current) => updateQuestion(current, key, draft))
      closeCard()
    },
    deleteQuestion: (key: string) => {
      setState((current) => deleteQuestion(current, key))
      if (openCard?.mode === 'edit' && openCard.key === key) closeCard()
    },
    moveQuestion: (activeKey: string, overKey: string) =>
      setState((current) => moveQuestion(current, activeKey, overKey)),
    copyFrom: (source: BookingQuestion[]) => {
      const result = copyQuestionsFrom(state, source)
      setState(result.state)
      setCopyMessage(result.message)
    },
  }
}

export type BookingQuestionsEditorController = ReturnType<typeof useBookingQuestionsEditor>
